import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ANSWERS } from '../content/answers';
import { eng, tr } from '../util/languageData';
import { isEnglishPath } from '../util/publicRoutes';
import { trackBlogCtaClick } from '../util/analytics';
import Seo from './Seo';
import styles from './answersPage.module.css';

/**
 * Short, quotable answers to the questions station owners ask (TURPAK and
 * bank reconciliation, card settlement timing, cash deposits, deficits,
 * fraud), each with steps, an example, its conditions and links to the page
 * that covers it in depth. Written for people first, and structured so search
 * engines and AI assistants can quote one answer on its own: every answer has
 * its own anchor (#id) and the page carries FAQPage structured data.
 * Turkish at /sss/, English at /en/faq/.
 */
function AnswersPage(): JSX.Element {
  const { pathname } = useLocation();
  const english = isEnglishPath(pathname);
  const content = english ? ANSWERS.en : ANSWERS.tr;
  const labels = (english ? eng : tr).answersPage;
  const prefix = english ? '/en' : '';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: content.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };

  return (
    <div className={styles.page}>
      <Seo
        path="/sss"
        enPath="/faq"
        title={content.title}
        description={content.description}
        schema={schema}
      />

      <header className={styles.hero}>
        <h1 className={styles.title}>{content.h1}</h1>
        <p className={styles.intro}>{content.intro}</p>
      </header>

      <div className={styles.container}>
        <nav className={styles.contents} aria-label={labels.contentsTitle}>
          <h2 className={styles.contentsTitle}>{labels.contentsTitle}</h2>
          <ol>
            {content.items.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.question}</a>
              </li>
            ))}
          </ol>
        </nav>

        {content.items.map((item) => (
          <section key={item.id} id={item.id} className={styles.item}>
            <h2 className={styles.question}>{item.question}</h2>
            <p className={styles.answer}>{item.answer}</p>

            {item.steps && item.steps.length > 0 && (
              <>
                <h3 className={styles.label}>{labels.stepsTitle}</h3>
                <ol className={styles.steps}>
                  {item.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </>
            )}

            {item.example && (
              <p className={styles.example}>
                <strong>{labels.exampleTitle}: </strong>
                {item.example}
              </p>
            )}

            {item.limitations && (
              <p className={styles.limitations}>
                <strong>{labels.limitationsTitle}: </strong>
                {item.limitations}
              </p>
            )}

            {item.links.length > 0 && (
              <p className={styles.links}>
                <span>{labels.readMore}: </span>
                {item.links.map((link, index) => (
                  <React.Fragment key={link.to}>
                    {index > 0 && ' · '}
                    <Link to={link.to}>{link.label}</Link>
                  </React.Fragment>
                ))}
              </p>
            )}
          </section>
        ))}

        <aside className={styles.cta}>
          <h2>{labels.ctaTitle}</h2>
          <p>{labels.ctaText}</p>
          <Link
            className={styles.ctaButton}
            to={`${prefix}/contact/`}
            onClick={() => trackBlogCtaClick('answers-page')}
          >
            {labels.ctaButton}
          </Link>
        </aside>
      </div>
    </div>
  );
}

export default AnswersPage;
